/**
 * PostHog feature flag keys: one registry every surface reads from, so the
 * gateway and the clients can't drift on a key.
 */
export const FEATURE_FLAGS = {
  /** `report_to_user` also reaches planner-spawned (staged-action) runs and is described by its purpose. Off, it is scheduled-skill only with the older description. */
  REPORT_TO_USER_OCT_NEW_BEHAVIOR: 'report-to-user-oct-new-behavior',
  /** Skills that opt in also DM what their scheduled runs deliver to the user in Slack, and Settings shows the toggle. */
  SKILL_SLACK_DELIVERY: 'skill-slack-delivery',
  /** Planner v2 (Jev) replaces planner v1 for the user's meetings. Gateway only. */
  PLANNER_V2_JEV: 'planner-v2-jev',
  /**
   * Planner v2 stages a Slack, Gmail or Calendar action blank, with questions on it asking where it goes, who it's
   * for and when, then fills and drafts it from the answers. Gateway only.
   */
  PLANNER_V2_ASK_FIRST: 'planner-v2-ask-first',
  /** The redesigned notch drops Send: a Slack, Gmail or Calendar action opens in its app with the draft filled in. */
  NOTCH_OPEN_IN_APP: 'notch-open-in-app',
  /** The assistant's final answer reveals while it streams, outside the tool fold, instead of after the turn ends. */
  CHAT_LIVE_FINAL_REVEAL: 'chat-live-final-reveal',
  /**
   * The chat prepare call. The app calls `POST /chat/prepare` when the user is in the composer, and the
   * conversation makes the next turn's reads and holds them for the send. Read by the app, to decide whether
   * to call, and by the gateway, which prepares nothing while it is off.
   */
  CHAT_PREPARE_TURN: 'chat-prepare-turn',
  /**
   * Gates user-added MCP servers. The gateway checks it on the `/mcp-servers` routes and before offering `mcp_*` or
   * `client_mcp_*` toolsets. The desktop checks it before running local servers or showing the settings section.
   */
  CUSTOM_MCP_SERVERS: 'custom-mcp-servers',
  /**
   * The October 2026 pricing revamp: Pro gains custom skills. Not read yet; the
   * LaunchDarkly flag of the same key gates it in the gateway and the desktop
   * app, and the two are flipped together until the gates move here.
   */
  V2_FREE_TIER_PLAN_REVAMP: 'v2_free_tier_plan_revamp',
  /**
   * Highlight's own sign-in pages (workspaces v4) in place of the hosted AuthKit page. Gateway only, read
   * with the public context before there is a user, so it is a per-environment switch scoped by evaluation
   * contexts. On, the desktop app's sign-in goes to `${FRONTEND_URL}/signin`, /v1/auth/web opens, and sign-in
   * decides new accounts' first workspace. Fails closed to AuthKit.
   */
  CUSTOM_SIGN_IN: 'custom-sign-in',
  /** The notch invites eligible new users to book a setup call, and Help offers booking one. Desktop only. */
  SETUP_CALL_INVITE: 'setup-call-invite',
  /** The setup-call invite and Help's booking entry skip users with a personal email provider such as Gmail. Desktop only. */
  SETUP_CALL_PROFESSIONAL_EMAIL_ONLY: 'setup-call-professional-email-only',
  /**
   * Which sidepanel a person gets. Multivariate: each variant names a profile in the desktop app's
   * `SIDEPANEL_PROFILES` (`classic`, `todos`), and an unknown variant or no answer is `classic`. A channel pin
   * or a developer override outranks it. Desktop only.
   */
  SIDEPANEL_PROFILE: 'sidepanel-profile',
  /** The sidepanel search bar, which searches meetings and chats and starts a chat with a picked model on Enter. Desktop only. */
  SIDEBAR_SEARCH: 'sidebar-search',
  /** A 1:1 Slack DM to the bot runs the orchestrator prompt, which replies at once and ticks a progress line as subagents finish. Gateway only. */
  SLACK_DM_ORCHESTRATOR: 'slack-dm-orchestrator',
  /**
   * The chat + menu's Attach File and Attach Folder, in place of Upload File. Desktop only, and only where the files
   * toolset is available.
   */
  ATTACH_LOCAL_FILES: 'attach-local-files',
  /** Attach File and Attach Folder carry a New badge with a tooltip saying what changed. Desktop only. */
  ATTACH_LOCAL_FILES_NEW_BADGE: 'attach-local-files-new-badge',
  /**
   * The desktop app learns the user's voice on-device from remote meetings. Multivariate, each variant adding to
   * the last: `fingerprint` only learns it, `match` also finds the user's voice in a meeting's mic, and
   * `consolidate` also relabels that voice as the user in the transcript and tells planner v2.
   */
  SELF_VOICE_MATCH: 'self-voice-match',
} as const

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS]
